import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { CultivationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-api';
import { LocationInputComponent, LocationSuggestion, suggestionName } from '../../../@commons/ui-components/location-input/location-input.component';
import { ChoiceOption, ChoiceSelectorComponent } from '../../../@commons/ui-components/choice-selector/choice-selector.component';
import { PosthogService } from '../../../@commons/@services/front/posthog.service';
import { GuestTrialService } from '../../../@commons/@services/front/guest-trial.service';
import { MapPinIconComponent } from '../../../@icons/map-pin-icon.component';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { loadErrorKind } from '../../../@commons/@services/data/load-state';
import { labelText } from '../../../@commons/utils/label-text.util';
import { lastFileUrl } from '../../../garden/@components/last-file-url.util';
import routesConstants from '../../../@commons/constants/routes.constants';
import { DayMessageComponent } from '../day-message/day-message.component';
import { DayMessage } from '../day-message/day-message.model';
import { chooseDayMessages } from '../day-message/day-message.engine';

/** Deux cultures, comme `POST advices/preview`. */
const MAX_CROPS = 2;

type TrialState = 'idle' | 'loading' | 'ready' | 'empty' | 'unknown-commune' | 'busy' | 'error';

/**
 * L'essai sans compte (`vw-guest-trial`) : sa commune et deux cultures donnent
 * le message de ce matin, avant toute inscription. Les cultures proposées
 * sont les plus semées (`home.guest.crops`), cherchées dans le catalogue ; le
 * message vient de `advices/preview` et passe par le même moteur que celui
 * d'un compte. Pas de réponse sans compte : le bouton mène à l'inscription.
 */
@Component({
  selector: 'vw-guest-trial',
  standalone: true,
  imports: [TranslatePipe, LocationInputComponent, ChoiceSelectorComponent, DayMessageComponent, MapPinIconComponent],
  templateUrl: './guest-trial.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: AdviceApi, useClass: AdviceApi },
    { provide: CultivationApi, useClass: CultivationApi },
  ],
})
export class GuestTrialComponent implements OnInit {
  private readonly adviceApi = inject(AdviceApi);
  private readonly cultivationApi = inject(CultivationApi);
  private readonly translate = inject(TranslateService);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);
  private readonly posthog = inject(PosthogService);
  private readonly trial = inject(GuestTrialService);

  readonly maxCrops = MAX_CROPS;
  commune = '';
  crops: ChoiceOption[] = [];
  chosen: string[] = [];
  state: TrialState = 'idle';
  message: DayMessage | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadCrops();
  }

  /** Les cultures proposées, dans la langue de l'app ; une culture introuvable est sautée. */
  private async loadCrops(): Promise<void> {
    const lang = this.storage.getCurrentLanguage();
    const names = String(this.translate.instant('home.guest.crops')).split('|').map((n) => n.trim()).filter(Boolean);
    const found = await Promise.all(
      names.map(async (name) => {
        try {
          const page = await this.cultivationApi.list({ name } as any, 1, 1, lang);
          return page?.results?.[0] ?? null;
        } catch {
          return null;
        }
      }),
    );
    this.crops = found
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map((c) => ({ value: c.uid, label: labelText(c.name as any, lang), imageUrl: lastFileUrl(c, '') || undefined }));
  }

  /** La commune choisie dans la liste : son nom (« Liège »), pas le libellé complet. */
  onCommune(feature: LocationSuggestion): void {
    this.commune = suggestionName(feature);
    void this.preview();
  }

  onCrops(value: string[]): void {
    this.chosen = value;
    void this.preview();
  }

  /** Commune et deux cultures choisies : le message de ce matin. */
  async preview(): Promise<void> {
    if (!this.commune.trim() || this.chosen.length !== MAX_CROPS) return;
    this.state = 'loading';
    const lang = this.storage.getCurrentLanguage();
    try {
      const advice = await this.adviceApi.preview({ commune: this.commune.trim(), cultivationUids: this.chosen }, lang);
      const choice = chooseDayMessages({
        now: new Date(), lang: lang.toLowerCase(), advice, alerts: [], greenLights: [], plantActions: [], plants: null, thermal: [], counts: null,
      });
      // Sans météo du jour, l'essai ne dit rien de rassurant : il le dit comme une panne.
      this.message = choice.weatherFresh ? choice.head : null;
      this.state = !choice.weatherFresh ? 'error' : this.message ? 'ready' : 'empty';
      this.posthog.capture('guest_trial_previewed', { hasMessage: !!this.message });
    } catch (e) {
      const kind = loadErrorKind(e);
      this.message = null;
      this.state = kind === 'notFound' ? 'unknown-commune' : kind === 'busy' ? 'busy' : 'error';
    }
  }

  /** L'inscription garde l'essai : l'onboarding reprend la commune et les deux cultures. */
  signup(): void {
    this.trial.save({ commune: this.commune.trim(), cultivationUids: this.chosen });
    this.posthog.capture('guest_signup_clicked', { placement: 'home_trial' });
    void this.router.navigate([routesConstants.REGISTER]);
  }
}
